import { Modal } from 'components/common/Modal/Modal';
import i18n from 'i18n';
import styles from './outdoorDeviceReport.module.scss';
import { useCallback, useMemo, useState } from 'react';
import { ToggleButtonField } from 'components/common/form/field/ToggleButtonField';
import { CustomField } from 'components/common/form/field/CustomField';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import { DateRangePicker } from 'components/common/DateRangePicker/DateRangePicker';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  KEY_COLUMN,
  useOutdoorDeviceReportModel
} from './OutdoorDeviceReportModel';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { commaDecorator } from 'components/ReportTable/ReportDataHelper';
import { ChartOptions } from 'chart.js';
import config from 'config';
import { GenericChart } from 'components/common/Chart/GenericChart';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/bind';
import { OutdoorDeviceMetricType } from 'core/report/ReportData';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  WeatherVerificationFilters,
  WeatherVerificationResult
} from './WeatherVerification/WeatherVerificationTab';
import { useWeatherVerificationTabModel } from './WeatherVerification/WeatherVerificationTabModel';
const cx = classNames.bind(styles);

enum OutdoorReportType {
  PERFORMANCE = 'performance',
  WEATHER_VERIFICATION = 'weatherVerification'
}

export type OutdoorDeviceReportProps = {
  l1ObjectId: number;
  startDate: string;
  endDate: string;
  deliveryUnit?: DeliveryUnit;
  /**
   * 這個廣告群組所屬的銷售渠道是不是電子紙。
   *
   * 電子紙機台沒有天氣鎖定這回事，所以「報表類型」只留機台投遞成效；
   * 同時人流數也不適用，資料類型的收斂在 useOutdoorDeviceReportModel 裡。
   */
  isEpaper: boolean;
  /** 這個廣告群組底下綁了天氣鎖定的活動數，由上層從已載入的活動列表算出，顯示成 Tab badge */
  weatherTargetedCampaignCount: number;
  onClose: () => void;
};

export const OutdoorDeviceReport: React.FC<OutdoorDeviceReportProps> = ({
  l1ObjectId,
  startDate,
  endDate,
  deliveryUnit,
  isEpaper,
  weatherTargetedCampaignCount,
  onClose
}) => {
  const {
    loading,
    columns,
    metricType,
    metricTypeOptions,
    gran,
    granOptions,
    from,
    to,
    reportTableData,
    metricTotalSum,
    tableSummaryRecord,
    queryOptions,
    chartData,
    dayRangeError,
    onMetricTypeChange,
    onGranChange,
    onDayRangeChange,
    onQueryBtnClick
  } = useOutdoorDeviceReportModel(
    l1ObjectId,
    startDate,
    endDate,
    deliveryUnit,
    isEpaper
  );

  const [reportType, setReportType] = useState<OutdoorReportType>(
    OutdoorReportType.PERFORMANCE
  );
  const isWeatherVerification =
    reportType === OutdoorReportType.WEATHER_VERIFICATION;
  const weatherVerificationModel = useWeatherVerificationTabModel(
    l1ObjectId,
    startDate,
    endDate,
    isWeatherVerification
  );
  const onReportTypeChange = useCallback((value: string | number) => {
    setReportType(value as OutdoorReportType);
  }, []);
  const reportTypeOptions = useMemo(
    () => [
      {
        value: OutdoorReportType.PERFORMANCE,
        label: i18n.t('weatherVerification.labels.deliveryPerformance')
      },
      // 電子紙不做天氣鎖定，機台投遞驗證整個選項藏起來，只留投遞成效
      ...(isEpaper
        ? []
        : [
            {
              value: OutdoorReportType.WEATHER_VERIFICATION,
              label: i18n.t('weatherVerification.labels.deliveryVerification'),
              // 數量走 badge 呈現（見 renderReportTypeLabel）；0 時不顯示。
              // 這是活動的設定值，不能改用篩選區的活動下拉筆數 —— 下拉跟著查詢區間走，
              // 會讓使用者改日期時 badge 數字跟著跳。
              extra: weatherTargetedCampaignCount
            }
          ])
    ],
    [isEpaper, weatherTargetedCampaignCount]
  );
  const renderReportTypeLabel = useCallback(
    (option: SelectOptions) => (
      <>
        {option.label}
        {!!option.extra && (
          <span className={styles.tabBadge}>{option.extra}</span>
        )}
      </>
    ),
    []
  );

  const [showDimensionSelectArea, setShowDimensionSelectArea] = useState(true);
  const [showReportChart, setShowReportChart] = useState(true);
  const toggleShowDimensionSelectArea = useCallback(() => {
    setShowDimensionSelectArea((prev) => !prev);
  }, []);
  const toggleShowReportChart = useCallback(() => {
    setShowReportChart((prev) => !prev);
  }, []);

  const renderTitle = useCallback(() => {
    return (
      <div className={styles.titleArea}>
        <div className={styles.title}>
          {i18n.t('outdoorDeviceReport.title')}
        </div>
        <div className={styles.triggerAreaButtons}>
          <div
            className={styles.triggerAreaButton}
            onClick={toggleShowDimensionSelectArea}
          >
            <FontAwesomeIcon
              icon={showDimensionSelectArea ? faAngleDown : faAngleUp}
            />
            {i18n.t('report.labels.dimentSelectArea')}
          </div>
          <div
            className={styles.triggerAreaButton}
            onClick={toggleShowReportChart}
          >
            <FontAwesomeIcon icon={showReportChart ? faAngleDown : faAngleUp} />
            {i18n.t('report.labels.reportChartArea')}
          </div>
        </div>
      </div>
    );
  }, [
    showDimensionSelectArea,
    showReportChart,
    toggleShowDimensionSelectArea,
    toggleShowReportChart
  ]);

  const renderScheduling = useCallback((from: string, to: string) => {
    const endDateMoment = moment(to);
    const startDateMoment = moment(from);
    const days = endDateMoment.diff(startDateMoment, 'days');
    const hours = endDateMoment.diff(startDateMoment, 'hours');
    if (days > 0) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingDay'
          values={{ days: days + 1 }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    } else if (days === 0) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingHour'
          values={{ hours: hours + 1 }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    }
  }, []);

  const renderPerformanceUserOptions = useCallback(() => {
    return (
      <>
        <ToggleButtonField
          label={i18n.t('outdoorDeviceReport.labels.metricType')}
          name='metricType'
          value={metricType}
          options={metricTypeOptions}
          onChange={onMetricTypeChange}
        />
        <CustomField
          label={i18n.t('outdoorDeviceReport.labels.scheduledTimeRange')}
          name='scheduledTimeRange'
        >
          <div className={styles.reportGran}>
            <ToggleButtons
              name={'gran'}
              value={gran}
              options={granOptions}
              onChange={onGranChange}
            />
            <DateRangePicker
              minDate={startDate}
              maxDate={endDate}
              startDate={from}
              endDate={to}
              format={gran === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'}
              onChange={onDayRangeChange}
              showTimePicker={gran === 'hour'}
              popOverClassName={styles.dateRangePicker}
            />
            <span className={styles.hint}>{renderScheduling(from, to)}</span>
            {dayRangeError && (
              <span className={styles.errorHint}>{dayRangeError}</span>
            )}
          </div>
          <Button
            variant='primary'
            size='sm'
            onClick={onQueryBtnClick}
            disabled={!!dayRangeError}
          >
            {i18n.t('report.buttons.search')}
          </Button>
        </CustomField>
      </>
    );
  }, [
    startDate,
    endDate,
    from,
    to,
    gran,
    granOptions,
    metricType,
    metricTypeOptions,
    dayRangeError,
    onGranChange,
    onMetricTypeChange,
    renderScheduling,
    onQueryBtnClick,
    onDayRangeChange
  ]);

  const renderUserOptions = useCallback(() => {
    return (
      <>
        <ToggleButtonField
          label={i18n.t('weatherVerification.labels.reportType')}
          name='reportType'
          value={reportType}
          options={reportTypeOptions}
          onChange={onReportTypeChange}
          renderLabel={renderReportTypeLabel}
        />
        {isWeatherVerification ? (
          <WeatherVerificationFilters
            model={weatherVerificationModel}
            minDate={startDate}
            maxDate={endDate}
          />
        ) : (
          renderPerformanceUserOptions()
        )}
      </>
    );
  }, [
    reportType,
    reportTypeOptions,
    isWeatherVerification,
    weatherVerificationModel,
    startDate,
    endDate,
    onReportTypeChange,
    renderReportTypeLabel,
    renderPerformanceUserOptions
  ]);

  const getChartOptions = useCallback(
    (
      isEmpty: boolean,
      axisYInterval: number,
      labels: string[],
      currentMetricType: string
    ): ChartOptions<'bar'> => {
      const grid = {
        borderDash: [5, 2]
      };
      return {
        animation: {
          duration: 0
        },
        layout: {
          padding: {
            left: 5,
            right: 5
          }
        },
        maintainAspectRatio: false,
        scales: {
          x: {
            grid,
            offset: true,
            title: {
              display: true,
              text: i18n.t('outdoorDeviceReport.labels.timeSlot'),
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              maxRotation: 0,
              font: {
                size: 12
              },
              callback: function (tick, index, tickList) {
                const tickLength = Math.max(15, 100 / tickList.length);
                const label = labels[tick];
                return label.length > tickLength
                  ? `${label.substring(0, tickLength)}...`
                  : label;
              }
            }
          },
          left: {
            type: 'linear',
            position: 'left',
            grid,
            beginAtZero: true,
            title: {
              display: true,
              text: i18n.t(`outdoorDeviceMetric.labels.${currentMetricType}`),
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              stepSize: axisYInterval,
              font: {
                size: 12
              },
              maxTicksLimit: 5,
              callback: (tick: string | number) => {
                if (typeof tick === 'string' && isNaN(Number(tick))) {
                  return tick;
                }
                return commaDecorator(+tick);
              }
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: i18n.t(`outdoorDeviceReportChart.title.${currentMetricType}`),
            align: 'start',
            font: {
              size: 18,
              weight: 'bold'
            },
            padding: {
              bottom: 15
            }
          },
          datalabels: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            enabled: !isEmpty,
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems[0].label) {
                  return '';
                }
                return tooltipItems[0].label;
              },
              label: (tooltipItems) => {
                if (!tooltipItems.label) {
                  return '';
                }
                const metric = tooltipItems.dataset.label;
                return `${i18n.t(`outdoorDeviceMetric.labels.${metric}`)}: ${tooltipItems.formattedValue}`;
              }
            }
          },
          legend: {
            display: false
          }
        }
      };
    },
    []
  );

  const getMetricValueCeil = useCallback((value: number): number => {
    if (!value) {
      return 0;
    }
    const digits = value.toString().split('.')[0].length;
    const multiply = Math.pow(10, digits - 1);
    return Math.ceil(value / multiply) * multiply;
  }, []);

  const isEmpty = !tableSummaryRecord || loading;

  const renderChart = useCallback(() => {
    const metricType = queryOptions.metricType;
    const metricData: number[] = [];
    let metricMax = 0;
    const labels: string[] = [];
    chartData.forEach((data) => {
      const metricValue = data[metricType] as number;
      metricMax = metricMax > metricValue ? metricMax : metricValue;
      metricData.push(metricValue);
      labels.push(data.name);
    });
    const metricValueCeil = getMetricValueCeil(metricMax);
    const axisYInterval = metricValueCeil / 4;
    if (isEmpty || reportTableData.length === 1) {
      metricData.push(metricValueCeil - axisYInterval + 1);
    }

    const data = {
      labels,
      datasets: [
        {
          label: metricType,
          yAxisID: 'left',
          data: metricData,
          fill: false,
          borderColor: config.reportMetric1Color,
          backgroundColor: config.reportMetric1Color,
          lineTension: 0,
          borderWidth: 1
        }
      ]
    };
    const chartConfig = {
      data,
      options: getChartOptions(isEmpty, axisYInterval, labels, metricType)
    };
    return (
      <GenericChart<'bar'>
        type='bar'
        data={chartConfig.data}
        options={chartConfig.options}
        height={200}
      />
    );
  }, [
    isEmpty,
    chartData,
    queryOptions.metricType,
    reportTableData,
    getChartOptions,
    getMetricValueCeil
  ]);

  return (
    <Modal
      className={styles.outdoorDeviceReportModal}
      fullScreen={true}
      animation={false}
      secondaryButton={{
        title: i18n.t('common.buttons.close'),
        callback: onClose
      }}
      dismiss={onClose}
    >
      <div className={styles.container}>
        {(loading || weatherVerificationModel.loading) && <LoadingIndicator />}
        {renderTitle()}
        {showDimensionSelectArea && (
          <div className={styles.userOptions}>{renderUserOptions()}</div>
        )}
        {isWeatherVerification ? (
          <div className={styles.reportTable}>
            <WeatherVerificationResult model={weatherVerificationModel} />
          </div>
        ) : (
          <>
            {showReportChart && (
              <div className={styles.summary}>
                <div className={styles.total}>
                  <div className={styles.title}>
                    {queryOptions.metricType ===
                    OutdoorDeviceMetricType.E_PUBLISHER_IMPRES
                      ? i18n.t(
                          'outdoorDeviceReport.labels.ePublisherImpresTotal'
                        )
                      : i18n.t('outdoorDeviceReport.labels.impresTotal')}
                  </div>
                  <div className={styles.value}>
                    {commaDecorator(metricTotalSum)}
                  </div>
                </div>
                <div className={styles.chart}>{renderChart()}</div>
              </div>
            )}
            <div className={cx(styles.reportTable, { needTopBorder: isEmpty })}>
              <StickableTanstackTable
                keyField={KEY_COLUMN}
                data={reportTableData}
                columns={columns}
                noDataIndication={i18n.t('common.labels.noData')}
                defaultSorted={[
                  {
                    dataField: KEY_COLUMN,
                    order: 'desc'
                  }
                ]}
                hidePagination={true}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
